// @ts-ignore
//------------------------------------------------------------
// File:      DataLookup.ts
// Author:    Tony Henderson
// Generator: ElevenFiftyNine Automated Tool Chain  
// Project:   https://github.com/BinaryBlox-Schedule-API
//------------------------------------------------------------

// Entity State: DataLookup Entity State 
import { createSlice, PayloadAction, createEntityAdapter, EntityState, EntityAdapter, EntitySelectors } from '@reduxjs/toolkit'
import { ApiEntity, ApiEntityState } from '../index';
import { entityRequests } from '../effects/DataLookup.effects';


/**
* Redux: Entity Interfaces
*/
export interface CountyEntity extends ApiEntity {}   
export interface DistrictEntity extends ApiEntity {}   
export interface RegionEntity extends ApiEntity {}   

/**
* Redux: State Interfaces
*/
export interface CountyState extends ApiEntityState<CountyEntity> {}; 
export interface DistrictState extends ApiEntityState<DistrictEntity> {}; 
export interface RegionState extends ApiEntityState<RegionEntity> {}; 


/**
* @summary State Interface DataLookupState.
* @name DataLookupState 
*/
export interface DataLookupState {
    county: CountyState; 
    district: DistrictState; 
    region: RegionState; 
};  


/**
* Redux: Entity adapters
*/

/**
* @summary <CountyEntity> Entity Adapter 
* @name countyEntityAdapter
* @description Redux Entity Adapter for type <CountyEntity>. 
* Note, Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field...
* @return EntityAdapter<CountyEntity> 
*/
const countyEntityAdapter: EntityAdapter<CountyEntity> = createEntityAdapter<CountyEntity>({
// NOTE: If we want to change selectId in the future...
//       Keep the 'all IDs' array sorted based on some value
// selectId: dataLookup => dataLookup.id, 
// sortComparer: (a, b) => a.someValue.localeCompare(b.someValue)
})
/**
* @summary <DistrictEntity> Entity Adapter 
* @name districtEntityAdapter
* @description Redux Entity Adapter for type <DistrictEntity>. 
* Note, Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field...
* @return EntityAdapter<DistrictEntity> 
*/
const districtEntityAdapter: EntityAdapter<DistrictEntity> = createEntityAdapter<DistrictEntity>({
// NOTE: If we want to change selectId in the future...
//       Keep the 'all IDs' array sorted based on some value
// selectId: dataLookup => dataLookup.id, 
// sortComparer: (a, b) => a.someValue.localeCompare(b.someValue)
})
/**
* @summary <RegionEntity> Entity Adapter 
* @name regionEntityAdapter
* @description Redux Entity Adapter for type <RegionEntity>. 
* Note, Since we don't provide `selectId`, it defaults to assuming `entity.id` is the right field...
* @return EntityAdapter<RegionEntity> 
*/
const regionEntityAdapter: EntityAdapter<RegionEntity> = createEntityAdapter<RegionEntity>({
// NOTE: If we want to change selectId in the future...
//       Keep the 'all IDs' array sorted based on some value
// selectId: dataLookup => dataLookup.id, 
// sortComparer: (a, b) => a.someValue.localeCompare(b.someValue)
})

/**
* @summary Aggregate EntityAdapter Interface DataLookupEntityAdapterList.
* @name DataLookupEntityAdapterList 
*/
const dataLookupEntityAdapterList = {
    county: countyEntityAdapter,
    district: districtEntityAdapter,
    region: regionEntityAdapter,
};  

/**
* @summary Initial state for CountyState.
* @name initialCountyState
* @description Initial state for CountyState. 
* @return CountyState populated with default data. 
*/ 
export const initialCountyState: CountyState = countyEntityAdapter.getInitialState(<CountyState>{
    ids: [],
    selectedEntity: null,
    selectedEntityId: 0,
    selectedEntityTypeId: 0,
    allItemsLoaded: false,
    isLoading: false,
    isUpdating: false,
    statusMessage: null,
    error: null
});

/**
* @summary Initial state for DistrictState.
* @name initialDistrictState
* @description Initial state for DistrictState. 
* @return DistrictState populated with default data. 
*/ 
export const initialDistrictState: DistrictState = districtEntityAdapter.getInitialState(<DistrictState>{
    ids: [],
    selectedEntity: null,
    selectedEntityId: 0,
    selectedEntityTypeId: 0,
    allItemsLoaded: false,
    isLoading: false,
    isUpdating: false,
    statusMessage: null,
    error: null
});

/**
* @summary Initial state for RegionState.
* @name initialRegionState
* @description Initial state for RegionState. 
* @return RegionState populated with default data. 
*/ 
export const initialRegionState: RegionState = regionEntityAdapter.getInitialState(<RegionState>{
    ids: [],
    selectedEntity: null,
    selectedEntityId: 0,
    selectedEntityTypeId: 0,
    allItemsLoaded: false,
    isLoading: false,
    isUpdating: false,
    statusMessage: null,
    error: null
});


/**
* @summary Initial state for DataLookupState.
* @name initialState
* @description Initial state for DataLookupState. 
* @return DataLookupState populated with default data. 
*/
export const initialState: DataLookupState =  <DataLookupState>{ 
    county: initialCountyState,  
    district: initialDistrictState,  
    region: initialRegionState,  
};




/**
* @summary Entity Slice for entity DataLookup
* @name entitySlice
* @description Entity Slice for entity DataLookup to created CRUD features. 
* @return entity slice of given entity type.
*/
const entitySlice = createSlice({
      name: 'dataLookupReducer',
      initialState,
      reducers: 
      {  
            getAll_County_GetAll_Data(state: DataLookupState) {
                  state.county = { ...state.county, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [getAll_County_GetAll_Data] recieved...`);
                  return state;
            },
            getAll_County_GetAll_Data_Success(state: DataLookupState, action: PayloadAction<any>) {
                  state.county = { ...state.county, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [getAll_County_GetAll_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  state.county = countyEntityAdapter.setAll(state.county, entityList);

                  return state;
            },
            getAll_County_GetAll_Data_Fault(state: DataLookupState, action: PayloadAction<any>) {
                  state.county = { ...state.county, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [getAll_County_GetAll_Data_Fault] recieved...`);
                  return state;  
            },
            getAll_District_GetAll_Data(state: DataLookupState) {
                  state.district = { ...state.district, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [getAll_District_GetAll_Data] recieved...`);
                  return state;
            },
            getAll_District_GetAll_Data_Success(state: DataLookupState, action: PayloadAction<any>) {
                  state.district = { ...state.district, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [getAll_District_GetAll_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  state.district = districtEntityAdapter.setAll(state.district, entityList);

                  return state;
            },
            getAll_District_GetAll_Data_Fault(state: DataLookupState, action: PayloadAction<any>) {
                  state.district = { ...state.district, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [getAll_District_GetAll_Data_Fault] recieved...`);
                  return state;  
            },
            getAll_Region_GetAll_Data(state: DataLookupState) {
                  state.region = { ...state.region, isLoading: true, statusMessage: 'fetching' };
                  console.log(`Action [getAll_Region_GetAll_Data] recieved...`);
                  return state;
            },
            getAll_Region_GetAll_Data_Success(state: DataLookupState, action: PayloadAction<any>) {
                  state.region = { ...state.region, isLoading: false, error: null, statusMessage: 'success' }; 
                  console.log(`Action [getAll_Region_GetAll_Data_Success] recieved...`);
                  let response = action.payload;
                  let entityList = (response && response.responseBody && response.responseBody.data) ? action.payload.responseBody.data : []; 
                  state.region = regionEntityAdapter.setAll(state.region, entityList);

                  return state;
            },
            getAll_Region_GetAll_Data_Fault(state: DataLookupState, action: PayloadAction<any>) {
                  state.region = { ...state.region, isLoading: false, error: action.payload, statusMessage: 'failure' };
                  console.log(`Action [getAll_Region_GetAll_Data_Fault] recieved...`);
                  return state;  
            },

/**
* Extended actions, non-service for local select & delete
**/
            selectValue_County_Data_Action(state: DataLookupState, action: PayloadAction<any>) { 
                  state.county = { ...state.county, isLoading: false, selectedEntityId: action.payload, statusMessage: 'item selected'};

                  console.log(`Action select_action_County: ${action.payload}`);
                  return state;
            },
            clearValues_County_Data_Action(state: DataLookupState) { 
                  state.county = { ...state.county, isLoading: false, statusMessage: 'item selected' }; 
                  console.log(`Action [clear_action_County] clearing...`);
                  state.county = countyEntityAdapter.removeAll(state.county); 

                  return state;
            },
            selectValue_District_Data_Action(state: DataLookupState, action: PayloadAction<any>) { 
                  state.district = { ...state.district, isLoading: false, selectedEntityId: action.payload, statusMessage: 'item selected'};

                  console.log(`Action select_action_District: ${action.payload}`);
                  return state;
            },
            clearValues_District_Data_Action(state: DataLookupState) { 
                  state.district = { ...state.district, isLoading: false, statusMessage: 'item selected' }; 
                  console.log(`Action [clear_action_District] clearing...`);
                  state.district = districtEntityAdapter.removeAll(state.district); 

                  return state;
            },
            selectValue_Region_Data_Action(state: DataLookupState, action: PayloadAction<any>) { 
                  state.region = { ...state.region, isLoading: false, selectedEntityId: action.payload, statusMessage: 'item selected'};

                  console.log(`Action select_action_Region: ${action.payload}`);
                  return state;
            },
            clearValues_Region_Data_Action(state: DataLookupState) { 
                  state.region = { ...state.region, isLoading: false, statusMessage: 'item selected' }; 
                  console.log(`Action [clear_action_Region] clearing...`);
                  state.region = regionEntityAdapter.removeAll(state.region); 

                  return state;
            },
    } 
}); 


const { reducer } = entitySlice;

const { actions } = entitySlice;

export { actions as entityActions, entityRequests, reducer as entityReducer, dataLookupEntityAdapterList as entityAdapterList};
   